import React, { useMemo, useRef, useState } from "react";
import { bool, func, number, string } from "prop-types";

import api from "@api";

import { Button } from "@components/shared/Button";
import CollapsableContainer from "@components/shared/CollapsableContainer";
import CheckmarkIcon from "@components/shared/Icons/Checkmark";
import CircularLoader from "@components/shared/Icons/CircularLoader";
import WarningIcon from "@components/shared/Icons/Warning";
import Typography from "@components/shared/Typography";
import CustomerCreationForm from "./CustomerCreationForm";
import { basketPersonalContinueEcommerceEvent } from "@gtm/events/basket";

import { useDebounce } from "@hooks/useDebounce";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { useBrandIdSettingsSelector } from "@redux/reducers/appConfig";
import { getBrandIdLoginRedirect } from "@lib/getBrandIdLoginRedirect";
import { redirectToExternalURL } from "@shared/v2/lib/redirectToExternalURL";

import { Trans, useTranslation } from "@lib/i18n";

import { EMAIL_REGEX } from "@shared/constants";

import gtm from "@gtm/core";

const INPUT_STATUS = {
  NONE: "NONE",
  CHECKING: "CHECKING",
  INVALID: "INVALID",
  VALID: "VALID",
};

// values are camelCased because they will be used in translation keys
const ACCOUNT_STATUS = {
  ACTIVATED: "activated",
  NOT_ACTIVATED: "notActivated",
  TO_BE_CREATED: "toBeCreated",
};

const UserAuthentification = props => {
  const {
    defaultOpen,
    disabled,
    index,
    isHeadTextHidden = false,
    isOpen,
    onEditingStart,
    onSubmitSuccessCallback,
    registrationType = null,
    redirectLogin,
    containerOnChange,
  } = props;

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const debounce = useDebounce();
  const isB2BSelected = useIsB2BSelected();
  const brandIDConfig = useBrandIdSettingsSelector();

  const [accountStatus, setAccountStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [inputStatus, setInputStatus] = useState(INPUT_STATUS.NONE);
  const [isButtonWorking, setIsButtonWorking] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isRetryAvailable, setIsRetryAvailable] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  const inputRef = useRef(null);
  const latestFetchId = useRef(0);

  const inputIcon = useMemo(() => {
    switch (inputStatus) {
      case INPUT_STATUS.CHECKING:
        return <CircularLoader size="default" />;
      case INPUT_STATUS.VALID:
        return <CheckmarkIcon checked={true} className="text-green" />;
      case INPUT_STATUS.INVALID:
        return <WarningIcon className="text-red" />;
      case INPUT_STATUS.NONE:
      default:
        return null;
    }
  }, [inputStatus]);

  const checkEmail = async email => {
    if (email.length < 6) {
      setInputStatus(INPUT_STATUS.NONE);
      setAccountStatus(null);
      setErrorMessage("");
      setIsRetryAvailable(false);
      return;
    }

    debounce(async () => {
      const valid = EMAIL_REGEX.test(email);

      if (!valid) {
        setInputStatus(INPUT_STATUS.INVALID);
        setErrorMessage(
          t("basket.personalDetails.emailCheck.input.error.invalidFormat")
        );
        setAccountStatus(null);
        return;
      }

      if (valid) {
        setInputStatus(INPUT_STATUS.CHECKING);
        setErrorMessage("");
        setIsRetryAvailable(false);

        const currentFetchId = Date.now();
        latestFetchId.current = currentFetchId;

        try {
          const result = await api.postCheckEmail(email);
          if (currentFetchId === latestFetchId.current) {
            const { accountFound, accountActivated } = result.data;

            setUserEmail(email);

            if (!accountFound) {
              setAccountStatus(ACCOUNT_STATUS.TO_BE_CREATED);
            } else {
              setAccountStatus(
                accountActivated
                  ? ACCOUNT_STATUS.ACTIVATED
                  : ACCOUNT_STATUS.NOT_ACTIVATED
              );
            }

            setInputStatus(INPUT_STATUS.VALID);
          }
        } catch (err) {
          if (currentFetchId === latestFetchId.current) {
            setInputStatus(INPUT_STATUS.INVALID);
            setErrorMessage(
              t("basket.personalDetails.emailCheck.input.error.api")
            );
            setIsRetryAvailable(true);
            console.error(err);
          }
        }
      }
    }, 300);
  };

  const retryCheckEmail = e => {
    e.preventDefault();

    if (!inputRef.current) {
      return;
    }

    const value = inputRef.current.value?.trim();

    checkEmail(value);
  };

  const handleInput = e => {
    setErrorMessage("");
    setIsRetryAvailable(false);

    checkEmail(e.currentTarget.value.trim());
  };

  const handleProceedButton = e => {
    e.preventDefault();

    switch (accountStatus) {
      case ACCOUNT_STATUS.ACTIVATED:
      case ACCOUNT_STATUS.NOT_ACTIVATED:
        setIsButtonWorking(true);

        if (registrationType) {
          gtm.fire(
            basketPersonalContinueEcommerceEvent({
              personalOption: "Login",
              isB2BSelected,
            })
          );
        }

        redirectToExternalURL(
          getBrandIdLoginRedirect(redirectLogin, brandIDConfig, language)
        );
        break;
      case ACCOUNT_STATUS.TO_BE_CREATED:
        setIsCreatingAccount(true);
        break;
    }
  };

  const resetView = () => {
    setIsCreatingAccount(false);
    setInputStatus(INPUT_STATUS.NONE);
    setAccountStatus(null);
    setErrorMessage("");
    setIsRetryAvailable(false);
  };

  return (
    <CollapsableContainer
      defaultOpen={defaultOpen}
      isDisabled={disabled}
      isOpen={isOpen}
      isHeadTextHidden={isHeadTextHidden}
      title={t("basket.personalDetails.selectTitle")}
      titleIndex={index}
      onChange={containerOnChange}
    >
      <div className="flex flex-col flex-wrap justify-around items-stretch gap-8 w-full sm:justify-between">
        {!isCreatingAccount ? (
          <>
            <Typography className="text-sm">
              {t("basket.personalDetails.emailCheck.intro")}
            </Typography>
            <div className="flex flex-wrap w-full">
              <label className="text-sm mb-2" htmlFor="user-email-check-input">
                {t("basket.personalDetails.emailCheck.input.label")}
              </label>
              <div className="relative w-full pb-1">
                <input
                  className="bg-white border-2 border-greyLight rounded-sm text-sm/5 p-2 pr-10 w-full"
                  data-id="user-email-check-input"
                  id="user-email-check-input"
                  onInput={handleInput}
                  placeholder={t(
                    "basket.personalDetails.emailCheck.input.placeholder"
                  )}
                  ref={inputRef}
                />
                <div className="grid place-items-center size-6 right-2 top-1/2 -translate-y-1/2 -mt-px select-none pointer-events-none absolute">
                  {inputIcon}
                </div>
              </div>
              <Typography className="text-xs/4 mt-1 pl-2">
                {errorMessage}{" "}
                {isRetryAvailable && (
                  <button
                    className="text-primaryOrange underline cursor-pointer"
                    onClick={retryCheckEmail}
                  >
                    {t("basket.personalDetails.emailCheck.input.retry")}
                  </button>
                )}
              </Typography>
            </div>
            {accountStatus !== null && (
              <div className="flex flex-col items-start gap-2 w-full pb-1">
                <Typography className="font-semibold">
                  {t(
                    `basket.personalDetails.emailCheck.checked.${accountStatus}.title`
                  )}
                </Typography>
                <Typography className="text-sm">
                  <Trans>
                    {t(
                      `basket.personalDetails.emailCheck.checked.${accountStatus}.text`
                    )}
                  </Trans>
                </Typography>
                <Button
                  className="self-center mt-4 min-w-25"
                  data-id="user-email-check-button"
                  onClick={handleProceedButton}
                  isLoading={isButtonWorking}
                  size="small"
                  variant="secondary"
                >
                  {t(
                    `basket.personalDetails.emailCheck.checked.${accountStatus}.button`
                  )}
                </Button>
              </div>
            )}
          </>
        ) : (
          <CustomerCreationForm
            changeEmail={resetView}
            email={userEmail}
            onEditingStart={onEditingStart}
            onSubmitSuccessCallback={onSubmitSuccessCallback}
          />
        )}
      </div>
    </CollapsableContainer>
  );
};

UserAuthentification.propTypes = {
  defaultOpen: bool,
  disabled: bool.isRequired,
  index: number,
  isCurrentStep: bool,
  isHeadTextHidden: bool,
  isOpen: bool,
  onEditingStart: func,
  onSubmitSuccessCallback: func,
  registrationType: number,
  redirectLogin: string.isRequired,
  containerOnChange: func,
};

export default UserAuthentification;
